import React from 'react';
import { 
  Send, Bot, Satellite, Flame, Droplets, Wheat, 
  Building2, Sparkles, ShieldCheck, CheckCircle2, 
  ExternalLink, Bell, FileText, ArrowUpRight, Zap, TreePine
} from 'lucide-react';
import { Language } from '../utils/i18n';

interface TelegramBotsShowcaseProps {
  language: Language;
}

export const TelegramBotsShowcase: React.FC<TelegramBotsShowcaseProps> = ({ language }) => {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isDe = language === 'de';
  const isFr = language === 'fr';

  const t = {
    badge: isUk ? '🛰️ АВТОНОМНА СУПУТНИКОВА МЕРЕЖА TELEGRAM' :
           isPl ? '🛰️ AUTONOMICZNA SIEĆ SATELITARNA TELEGRAM' :
           isDe ? '🛰️ AUTONOMES TELEGRAM-SATELLITENNETZWERK' :
           isFr ? '🛰️ RÉSEAU SATELLITAIRE AUTONOME TELEGRAM' :
           '🛰️ AUTONOMOUS TELEGRAM SATELLITE NETWORK',
    
    title: isUk ? 'Супутникові Вартові Deep Research Hub у Telegram' :
           isPl ? 'Satelitarni Strażnicy Deep Research Hub na Telegramie' :
           isDe ? 'Satelitengestützte Telegram-Wächter von Deep Research Hub' :
           isFr ? 'Gardiens Satellitaires Deep Research Hub sur Telegram' :
           'Deep Research Hub Autonomous Satellite Bots in Telegram',

    subtitle: isUk 
      ? 'Отримуйте супутниковий моніторинг полів, просідання будівель, пожеж та водойм 24/7, миттєві Push-алерти тривоги та офіційні Акти й Паспорти Word (ДСТУ 4163:2020) прямо у Вашому смартфоні.' 
      : isPl 
      ? 'Otrzymuj monitoring pól, osiadania budynków, pożarów i zbiorników wodnych 24/7, natychmiastowe alerty push oraz oficjalne raporty Word na swoim smartfonie.'
      : isDe 
      ? 'Erhalten Sie 24/7-Satellitenüberwachung für Landwirtschaft, Bodensenkungen, Brände und Gewässer mit Push-Benachrichtigungen und offiziellen Berichten.'
      : isFr 
      ? 'Surveillance satellitaire 24/7 pour l\'agriculture, les affaissements, les incendies et les réservoirs avec alertes instantanées et rapports officiels.'
      : 'Get 24/7 autonomous satellite monitoring for agriculture, ground subsidence, wildfire hazards, and water bodies with instant push alerts and official Word reports.',

    freeBadge: isUk ? '🎁 1-Й АНАЛІЗ БЕЗКОШТОВНО' : isPl ? '🎁 1. ANALIZA GRATIS' : '🎁 1ST AUDIT FREE',
    launchBtn: isUk ? 'Запустити в Telegram' : isPl ? 'Uruchom na Telegramie' : isDe ? 'In Telegram starten' : isFr ? 'Lancer sur Telegram' : 'Launch in Telegram',
    authorBadge: isUk ? 'Автор та патентовласник 100% IP: Банах А.С. (ORCID: 0009-0000-2933-1143)' : 'Author & 100% IP Owner: Andriy Banakh (ORCID: 0009-0000-2933-1143)',
  };

  const bots = [
    {
      id: 'agro',
      name: 'Agro Deep Research Hub',
      handle: '@AgroDeepResearchHub_bot',
      url: 'https://t.me/AgroDeepResearchHub_bot',
      icon: Wheat,
      color: 'emerald',
      gradient: 'from-emerald-500/20 via-teal-500/10 to-transparent',
      borderColor: 'border-emerald-500/40 hover:border-emerald-400',
      badgeBg: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
      source: 'Copernicus Sentinel-2 MSI (10 м)',
      role: isUk ? 'Супутниковий Агроном & Моніторинг Полів' : isPl ? 'Agronom Satelitarny & Monitor Pól' : 'Satellite Agronomist & Field Monitor',
      desc: isUk 
        ? 'Спектральний аналіз стану посівів за індексами NDVI, NDRE, EVI, MSAVI. Оцінка вологоємності ґрунту, хлорофілу та прогнозу врожайності. Офіційний Агро-Паспорт поля Word (ДСТУ 4163:2020) та алерти вегетаційного стресу.'
        : isPl
        ? 'Analiza spektralna NDVI, NDRE, EVI, wilgotności gleby i prognozy plonów. Oficjalny Agropaszport pola Word i alerty stresu wegetacyjnego.'
        : 'Vegetation indices NDVI, NDRE, EVI, soil moisture, and crop yield forecasting. Official Word field passport (DSTU 4163:2020) and vegetation stress alerts.',
      features: isUk ? [
        'Спектральні індекси NDVI, NDRE, EVI, MSAVI (10 м)',
        'Вологоємність ґрунту, хлорофіл та прогноз врожайності',
        'Офіційний Агро-Паспорт поля Word (.docx ДСТУ 4163:2020)',
        'Цілодобовий Watchlist полів та Push-алерти посухи'
      ] : [
        'Sentinel-2 spectral indices NDVI, NDRE, EVI (10m)',
        'Soil moisture, chlorophyll & yield projection',
        'Official Word field passport (.docx DSTU 4163:2020)',
        '24/7 watchlist & drought/stress push notifications'
      ],
      pricing: isUk ? '🎁 1-й безкоштовно • Разово: 100 ₴ • PRO: 390 ₴/міс' : '🎁 1st Free • Single: 100 UAH • PRO: 390 UAH/mo'
    },
    {
      id: 'insar',
      name: 'InSAR Deep Research Hub',
      handle: '@InSARDeepResearchHub_bot',
      url: 'https://t.me/InSARDeepResearchHub_bot',
      icon: Building2,
      color: 'blue',
      gradient: 'from-blue-500/20 via-indigo-500/10 to-transparent',
      borderColor: 'border-blue-500/40 hover:border-blue-400',
      badgeBg: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
      source: 'Sentinel-1 SAR & Copernicus EGMS (±1.65 мм)',
      role: isUk ? 'Радарний Вартовий Просідання Ґрунтів & Споруд' : isPl ? 'Radarowy Strażnik Osiadania Gruntu & Budowli' : 'Radar Satellite Ground Subsidence Watchdog',
      desc: isUk 
        ? 'Космічна радарна інтерферометрія міліметрової точності (PS-InSAR / DInSAR). Детекція просідання фундаментів, мостів, дамб, шахтних підробок та карстових провалів. Офіційний інженерно-геодезичний Висновок ДСТУ 4163:2020.'
        : isPl
        ? 'Precyzyjna interferometria radarowa InSAR (±1.65 mm/rok). Wykrywanie osiadania fundamentów, mostów i zapadadlisk krasowych. Orzeczenie inżynieryjne Word.'
        : 'Millimeter-grade radar interferometry (PS-InSAR / DInSAR, ±1.65 mm/yr). Structural health monitoring for foundations, dams, bridges & sinkholes. Official Engineering Deed.',
      features: isUk ? [
        'Радарна інтерферометрія міліметрової точності (±1.65 мм/рік)',
        'Виявлення карстових провалів, осідання мостів та споруд',
        'Офіційний Інженерно-геодезичний Висновок (.docx ДСТУ)',
        'Цілодобовий моніторинг об\'єктів та аварійні алерти'
      ] : [
        'Millimeter-grade radar interferometry (±1.65 mm/yr)',
        'Sinkhole & structural subsidence early detection',
        'Official engineering inspection report (.docx DSTU)',
        '24/7 monitored watchlist & emergency alerts'
      ],
      pricing: isUk ? '🎁 1-й безкоштовно • Разово: 150 ₴ • PRO: 490 ₴/міс' : '🎁 1st Free • Single: 150 UAH • PRO: 490 UAH/mo'
    },
    {
      id: 'fire',
      name: 'Fire Deep Research Hub',
      handle: '@Firedeepresearchhub_bot',
      url: 'https://t.me/Firedeepresearchhub_bot',
      icon: Flame,
      color: 'amber',
      gradient: 'from-amber-500/20 via-orange-500/10 to-transparent',
      borderColor: 'border-amber-500/40 hover:border-amber-400',
      badgeBg: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
      source: 'NASA FIRMS VIIRS (375 м) • Оновлення 15 хв',
      role: isUk ? 'Супутниковий Вартовий Пожеж & Термальних Загроз' : isPl ? 'Satelitarny Strażnik Pożarów & Zagrożeń' : 'Wildfire & Thermal Threat Satellite Guard',
      desc: isUk 
        ? 'Оперативне виявлення відкритих займань, осередків лісових пожеж та прильотів кожні 15 хвилин. Розрахунок теплової потужності полум\'я FRP (МВт), вектор вітру Open-Meteo, тактичні 300 DPI картограми зон безпеки та Акт ДСТУ 4163:2020.'
        : isPl
        ? 'Wykrywanie pożarów i anomalii termicznych co 15 minut z radiometrów NASA VIIRS. Moc cieplna FRP (MW), wektor wiatru i oficjalny Akt Pożarowy.'
        : 'Near-real-time detection of wildfires and thermal anomalies every 15 minutes (NASA VIIRS). Fire Radiative Power (MW), wind plume vectors & 300 DPI tactical maps.',
      features: isUk ? [
        'Детекція термальних аномалій NASA FIRMS (кожні 15 хв)',
        'Розрахунок теплової потужності горіння (FRP МВт)',
        'Вектор вітру Open-Meteo та 300 DPI картограми зон безпеки',
        'Офіційний Пожежний Акт Word (.docx за ДСТУ 4163:2020)'
      ] : [
        'NASA FIRMS VIIRS thermal anomalies (every 15 min)',
        'Fire Radiative Power (MW) & heat severity metrics',
        'Wind dispersion vector & 300 DPI radial safety map',
        'Official Word Fire Inspection Deed (.docx DSTU 4163)'
      ],
      pricing: isUk ? '🎁 1-й безкоштовно • Разово: 100 ₴ • PRO: 390 ₴/міс' : '🎁 1st Free • Single: 100 UAH • PRO: 390 UAH/mo'
    },
    {
      id: 'aqua',
      name: 'Aqua Deep Research Hub',
      handle: '@Aquadeepresearchhub_bot',
      url: 'https://t.me/Aquadeepresearchhub_bot',
      icon: Droplets,
      color: 'cyan',
      gradient: 'from-cyan-500/20 via-sky-500/10 to-transparent',
      borderColor: 'border-cyan-500/40 hover:border-cyan-400',
      badgeBg: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
      source: 'Copernicus Sentinel-2 & Open-Meteo',
      role: isUk ? 'Супутниковий Вартовий Водойм & Рибних Господарств' : isPl ? 'Satelitarny Strażnik Zbiorników Wodnych & Rybactwa' : 'Water Bodies & Aquaculture Satellite Sentinel',
      desc: isUk 
        ? 'Супутниковий контроль площі водного дзеркала, цвітіння ціанобактерій (NDCI) та каламутності (NDTI). Моделювання поверхневої температури та розчинного кисню O₂ (мг/л). Запобігання літньої задухи риби (гіпоксія < 4.0 мг/л) та офіційний Гідропаспорт.'
        : isPl
        ? 'Kontrola lustra wody, zakwitu sinic i mętności. Modelowanie temperatury i tlenu rozpuszczonego O₂ (mg/l). Zapobieganie przyduchy ryb i Paszport Wodny Word.'
        : 'Water mirror mapping, cyanobacteria bloom (NDCI) and turbidity. Dissolved oxygen DO (mg/L) modeling, summer fish kill hypoxia prevention & Word Hydrological Passport.',
      features: isUk ? [
        'Індекси NDWI (дзеркало води), NDCI (хлорофіл-а), NDTI (каламутність)',
        'Розрахунок розчинного кисню O₂ (мг/л) та водний баланс (ET0)',
        'Раннє попередження літньої задухи риби (гіпоксія O₂ < 4.0 мг/л)',
        'Офіційний Гідрологічний Паспорт Word (.docx за ДСТУ 4163:2020)'
      ] : [
        'Sentinel-2 indices NDWI (water mirror), NDCI, NDTI',
        'Dissolved oxygen DO (mg/L) & FAO-56 water balance',
        'Early warning for fish hypoxia kill (O₂ < 4.0 mg/L)',
        'Official Word Hydrological Passport (.docx DSTU 4163)'
      ],
      pricing: isUk ? '🎁 1-й безкоштовно • Разово: 100 ₴ • PRO: 390 ₴/міс' : '🎁 1st Free • Single: 100 UAH • PRO: 390 UAH/mo'
    },
    {
      id: 'mushroom',
      name: 'Mushroom Radar Hub',
      handle: '@GribnikDeppResearchHub_bot',
      url: 'https://t.me/GribnikDeppResearchHub_bot',
      icon: TreePine,
      color: 'lime',
      gradient: 'from-lime-500/20 via-emerald-500/10 to-transparent',
      borderColor: 'border-lime-500/40 hover:border-lime-400',
      badgeBg: 'bg-lime-500/15 text-lime-300 border-lime-500/30',
      source: 'Copernicus ERA5 (0-7 см)',
      role: isUk ? 'Супутниковий Радар для Грибників України' : isPl ? 'Satelitarny Radar Grzybiarza' : 'Satellite Mushroom Radar of Ukraine',
      desc: isUk 
        ? 'Космічний розрахунок вологості ґрунту (0-7 см), накопичених опадів за 7 діб та температурних порогів міцелію. 300 DPI тактичні карти 139 районів, GPS-радар лісу поруч та офіційний Паспорт угідь ДСТУ 4163:2020.'
        : isPl
        ? 'Kosmiczny monitoring wilgotności gleby (0-7 cm) i opadów 7-dniowych. Mapy taktyczne 139 rejonów, radar GPS i oficjalny Paszport Grzybowy Word.'
        : 'Satellite soil moisture (0-7cm) & 7-day rainfall analysis across 139 districts. 300 DPI tactical condition maps, GPS nearest forest radar & Word passport.',
      features: isUk ? [
        'Індекс росту грибів MGI (0-100%) за даними Copernicus ERA5',
        'Тактичні карти сприятливості районів (300 DPI Matplotlib)',
        'Пошук найближчого лісу за GPS зі смартфона',
        'Офіційний Паспорт грибних угідь (.docx за ДСТУ 4163:2020)'
      ] : [
        'Mushroom Growth Index MGI via Copernicus ERA5 & Sentinel',
        'Tactical 300 DPI district condition maps (Matplotlib)',
        'Nearest forest radar via smartphone GPS location',
        'Official Word Forest Passport (.docx DSTU 4163:2020)'
      ],
      pricing: isUk ? '🎁 3 безкоштовно • Паспорт: 50 ₴ • PRO: 190 ₴' : '🎁 3 Free • Single: 50 UAH • PRO: 190 UAH'
    }
  ];

  return (
    <section className="w-full my-6 p-4 sm:p-6 md:p-8 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 border border-cyan-500/30 rounded-3xl shadow-2xl relative overflow-hidden text-left">
      {/* Decorative Glow Elements */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header Strip */}
      <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-slate-800">
        <div className="space-y-2 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-950/80 border border-cyan-500/40 rounded-full text-[10px] font-mono font-bold text-cyan-300 uppercase tracking-widest shadow-inner">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
            <span>{t.badge}</span>
          </div>

          <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
            <Send className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-400 shrink-0" />
            <span>{t.title}</span>
          </h2>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans">
            {t.subtitle}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row md:flex-col items-start md:items-end gap-2 shrink-0">
          <div className="px-3 py-1.5 bg-emerald-950/80 border border-emerald-500/40 rounded-xl text-[10px] font-mono font-black text-emerald-300 flex items-center gap-2 shadow-md">
            <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-pulse" />
            <span>{t.freeBadge}</span>
          </div>
          <span className="text-[10px] font-mono text-slate-400">
            {t.authorBadge}
          </span>
        </div>
      </div>

      {/* Grid of 5 Telegram Bots */}
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5 pt-6">
        {bots.map((bot) => {
          const Icon = bot.icon;
          return (
            <div 
              key={bot.id}
              className={`flex flex-col justify-between p-5 bg-slate-900/90 hover:bg-slate-850 border ${bot.borderColor} rounded-2xl transition-all duration-300 shadow-lg hover:shadow-cyan-500/10 hover:-translate-y-1 relative group`}
            >
              <div className="space-y-3.5">
                {/* Bot Icon & Header Badge */}
                <div className="flex items-start justify-between gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-md bg-gradient-to-br ${bot.gradient} border border-slate-700`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-bold border ${bot.badgeBg} truncate max-w-[170px]`}>
                    {bot.source}
                  </span>
                </div>

                {/* Bot Name & Handle */}
                <div>
                  <h3 className="text-sm sm:text-base font-black text-white tracking-tight group-hover:text-cyan-300 transition-colors">
                    {bot.name}
                  </h3>
                  <a 
                    href={bot.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[11px] font-mono font-bold text-cyan-400 hover:text-cyan-300 hover:underline flex items-center gap-1 mt-0.5"
                  >
                    <span>{bot.handle}</span>
                    <ArrowUpRight className="w-3 h-3" />
                  </a>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">
                    {bot.role}
                  </p>
                </div>

                {/* Short Description */}
                <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-3">
                  {bot.desc}
                </p>

                {/* Bullet Points */}
                <ul className="space-y-1 pt-2 border-t border-slate-800 text-[10px] text-slate-300 font-sans">
                  {bot.features.map((feat, idx) => (
                    <li key={idx} className="flex items-start gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" />
                      <span className="leading-tight">{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pricing & CTA Button */}
              <div className="pt-4 mt-4 border-t border-slate-800 space-y-2.5">
                <div className="text-[9.5px] font-mono text-amber-300/90 bg-slate-950/80 px-2 py-1 rounded-md border border-slate-800 text-center font-bold">
                  {bot.pricing}
                </div>

                <a
                  href={bot.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-2.5 px-3 bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 hover:from-cyan-500 hover:via-blue-500 hover:to-indigo-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md hover:shadow-cyan-500/25 transition-all flex items-center justify-center gap-2 group-hover:scale-[1.02]"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>{t.launchBtn}</span>
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Dispatcher Info & Web App Bridge */}
      <div className="relative z-10 mt-6 pt-4 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-slate-400 font-mono">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-cyan-400" />
          <span>
            {isUk 
              ? 'Єдиний диспетчер сповіщень та системний бот: @Antigravity_cli_contabo_bot'
              : 'Primary Notification Dispatcher & System Bot: @Antigravity_cli_contabo_bot'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 font-bold flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping inline-block" />
            <span>5 / 5 Боти Активні Online</span>
          </span>
          <span className="text-slate-500">|</span>
          <span className="text-slate-400">Plata by mono • Apple Pay / Google Pay</span>
        </div>
      </div>
    </section>
  );
};
